import PedidoVendaLink from "components/PedidoVendaLink";
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { format } from "utils/formatDate";
import ptBR from "date-fns/locale/pt-BR";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import { useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ReportsService from "services/reports";
import { dotToComma } from "utils/numberComma";
registerLocale("ptBR", ptBR);

import { DataGrid } from "@mui/x-data-grid";
import "react-datepicker/dist/react-datepicker.css";
import { exportToExcel } from "react-json-to-excel";
import { ptBR_DataGrid } from "utils/datagrid";
import { commaToDot } from "utils/numberComma";

const num = (value) => Number(commaToDot(value ?? 0)) || 0;

// Custo unitário da compra: produtos + frete (carregamento + frete + ICMS - adiantamento) / quantidade
const custoUnitarioCompra = (purchase) => {
  if (!purchase) return 0;

  const totalPagarFrete =
    num(purchase.carregamento) +
    num(purchase.valorFrete) +
    num(purchase.valorIcms) -
    num(purchase.adiantamentoFrete);

  let total = totalPagarFrete;
  let quantidade = 0;
  purchase.products?.forEach((produto) => {
    total += num(produto.price) * num(produto.quantity);
    quantidade += num(produto.quantity);
  });

  return quantidade > 0 ? total / quantidade : 0;
};

// Preço médio de venda: soma(preço x quantidade) / soma(quantidade) dos produtos da venda
const precoMedioVenda = (sale) => {
  let total = 0;
  let quantidade = 0;
  sale?.products?.forEach((produto) => {
    total += num(produto.price) * num(produto.quantity);
    quantidade += num(produto.quantity);
  });

  return quantidade > 0 ? total / quantidade : 0;
};

const formatMoeda = (valor) => dotToComma(valor.toFixed(2));

const formatData = (data) => (data ? format(new Date(data), "dd/MM/yyyy") : "");

const nomePessoa = (p) => p?.razaoSocial || p?.nomeFantasia || p?.nome || "";

// Colunas do relatório (tabela e Excel usam a mesma lista).
// obrigatoria: quando vazia, a célula é destacada como pendência.
const COLUNAS = [
  {
    campo: "numPedido",
    titulo: "Nº Pedido",
    valor: (r) => r.numPedido,
    obrigatoria: true,
  },
  { campo: "qtd", titulo: "QTD", valor: (r) => r.qtdFrutas, obrigatoria: true },
  {
    campo: "totalPedido",
    titulo: "Total do Pedido",
    valor: (r) => (r.totalFinal ? formatMoeda(Number(r.totalFinal)) : ""),
    obrigatoria: true,
  },
  {
    campo: "data",
    titulo: "Data de Emissão",
    valor: (r) => formatData(r.data),
    obrigatoria: true,
  },
  {
    campo: "dataDescarga",
    titulo: "Data de Descarga",
    valor: (r) => formatData(r.dataDescarga),
    obrigatoria: true,
  },
  {
    campo: "produto",
    titulo: "Produto",
    valor: (r) =>
      (r.products || [])
        .map((p) => p.product?.descricao)
        .filter(Boolean)
        .join(", "),
    obrigatoria: true,
  },
  {
    campo: "cliente",
    titulo: "Cliente",
    valor: (r) => nomePessoa(r.customer),
    obrigatoria: true,
  },
  {
    campo: "cidade",
    titulo: "Cidade",
    valor: (r) => r.customer?.cidade,
    obrigatoria: true,
  },
  {
    campo: "motorista",
    titulo: "Motorista",
    valor: (r) => r.motorista,
    obrigatoria: true,
  },
  { campo: "caminhao", titulo: "Caminhão", valor: (r) => r.caminhao },
  {
    campo: "cargueiro",
    titulo: "Classificador",
    valor: (r) => r.purchase?.cargueiro,
    obrigatoria: true,
  },
  {
    campo: "produtor",
    titulo: "Produtor",
    valor: (r) => nomePessoa(r.purchase?.supplier),
    obrigatoria: true,
  },
  {
    campo: "vendedor",
    titulo: "Vendedor",
    valor: (r) => r.user?.name,
    obrigatoria: true,
  },
  {
    campo: "pesoLiquido",
    titulo: "Peso Líquido",
    valor: (r) => r.pesoLiquido,
    obrigatoria: true,
  },
  {
    campo: "vlVenda",
    titulo: "Vl. Venda",
    valor: (r) => `R$ ${formatMoeda(precoMedioVenda(r))}`,
  },
  {
    campo: "vlCompra",
    titulo: "Vl. Compra",
    valor: (r) =>
      r.purchase ? `R$ ${formatMoeda(custoUnitarioCompra(r.purchase))}` : "",
    obrigatoria: true,
  },
  {
    campo: "media",
    titulo: "Média",
    valor: (r) => r.purchase?.media ?? r.media,
    obrigatoria: true,
  },
  {
    campo: "agenciador",
    titulo: "Agenciador",
    valor: (r) => nomePessoa(r.agent) || nomePessoa(r.purchase?.agent),
  },
];

const vazio = (v) => v === undefined || v === null || v === "";

const camposEmBranco = (row) =>
  COLUNAS.filter((c) => c.obrigatoria && vazio(c.valor(row))).map(
    (c) => c.titulo
  );

export default function CarregamentoDia() {
  const [dia, setDia] = useState(new Date());
  const [extrato, setExtrato] = useState([]);
  const [loading, setLoading] = useState(true);

  async function fetchExtrato(changeDia = null) {
    setLoading(true);

    try {
      const data = await ReportsService.carregamentoDia(
        changeDia ? changeDia : dia.toISOString()
      );

      setExtrato(data);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao buscar extrato:", error);
      setLoading(false);
    }
  }

  const handleDateChange = async (date) => {
    setDia(date);
    if (!loading) fetchExtrato(date.toISOString());
  };

  const printReport = (report) => {
    exportToExcel(report, "carregamento_dia_" + new Date().getTime(), true);
  };

  const cleanRows = (rows) =>
    rows.map((row) =>
      COLUNAS.reduce((obj, c) => ({ ...obj, [c.titulo]: c.valor(row) ?? "" }), {
        "Campos em branco": camposEmBranco(row).join(", "),
      })
    );

  const gridColumns = COLUNAS.map((c) => ({
    field: c.campo,
    headerName: c.titulo,
    minWidth: 120,
    flex: 1,
    sortable: false,
    renderCell: (params) => {
      const v = c.valor(params.row);
      if (c.obrigatoria && vazio(v)) {
        return (
          <Tooltip title="Campo em branco">
            <Box
              sx={{
                width: "100%",
                bgcolor: "#fdecea",
                color: "#d32f2f",
                fontWeight: 600,
                px: 1,
                borderRadius: 1,
              }}
            >
              em branco
            </Box>
          </Tooltip>
        );
      }
      if (c.campo === "numPedido") {
        return <PedidoVendaLink saleId={params.row._id}>{v}</PedidoVendaLink>;
      }
      return <Typography variant="body2">{v}</Typography>;
    },
  }));

  const pedidosComPendencia = extrato.filter(
    (row) => camposEmBranco(row).length > 0
  ).length;

  useEffect(() => {
    fetchExtrato();
  }, []);

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <MDTypography variant="h6" color="white" mt={1}>
                  Carregamento do Dia
                </MDTypography>
              </MDBox>
              <MDBox pt={3} mx={2} py={3} px={2}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1fr",
                  }}
                >
                  <Typography variant="h4" gutterBottom>
                    Carregamento do dia: ({format(dia, "dd/MM/yyyy")})
                  </Typography>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: 8,
                    }}
                  >
                    <DatePicker
                      selected={dia}
                      onChange={handleDateChange} //only when value has changed
                      locale="ptBR"
                      dateFormat="dd/MM/yyyy"
                      customInput={<TextField style={{ paddingLeft: 4 }} />}
                    />
                  </div>
                </Box>
                {loading && (
                  <Typography variant="body1">Carregando...</Typography>
                )}
                {!loading && pedidosComPendencia > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <b>Atenção:</b> {pedidosComPendencia} pedido(s) com campos
                    em branco (destacados em vermelho na tabela).
                  </Alert>
                )}
                {!loading && (
                  <DataGrid
                    rows={extrato}
                    // empty
                    localeText={ptBR_DataGrid}
                    sx={{
                      ".MuiDataGrid-virtualScroller": {
                        minHeight: "100px",
                      },
                    }}
                    columns={gridColumns}
                    pageSize={5}
                    componentsProps={{
                      cell: {
                        style: {
                          fontWeight: "bold",
                        },
                      },
                    }}
                    getRowId={(row) => row?._id}
                  />
                )}
              </MDBox>
            </Card>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() =>
                  printReport([
                    {
                      sheetName: "Carregamento do Dia",
                      details: cleanRows(extrato),
                    },
                  ])
                }
                sx={{
                  color: "white !important",
                }}
              >
                Exportar Relatório Para Excel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
